import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import ReactPixel from "react-facebook-pixel";

// your util for anonymous device id
import { getHostName, getAnonUserId } from "../helpers/utils";

const CartWishlistContext = createContext();

export const CartWishlistProvider = ({ children }) => {
  const hostname = getHostName();

  const [cartList, setCartList] = useState([]);
  const [wishList, setWishList] = useState([]);
  const [cartListCounter, setCartListCounter] = useState(0);
  const [wishListCounter, setWishListCounter] = useState(0);

  // ✅ APIs

  const normalizeApiList = (payload) => {
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
  };

  const getAllCartListData = () => {
    const anonUserId = getAnonUserId();
    axios
      .post(`${hostname}/api/cart/get`, { annoUserDeviceId: anonUserId })
      .then((res) => {
        const arr = normalizeApiList(res.data).filter((v) => v.status);
        setCartList(arr);
      })
      .catch((err) => console.log(err));
  };

  const getAllWishListData = () => {
    const anonUserId = getAnonUserId();
    if (!anonUserId) return;
    axios
      .post(`${hostname}/api/wishlist/get-wishlist`, {
        annoUserDeviceId: anonUserId,
      })
      .then((res) => {
        const arr = normalizeApiList(res.data).filter((v) => v.status);
        setWishList(arr);
      })
      .catch((err) => console.log(err));
  };

  const getCartCounterData = () => {
    const anonUserId = getAnonUserId();
    axios
      .get(`${hostname}/api/cart/count?annoUserDeviceId=${anonUserId}`)
      .then((res) => setCartListCounter(res.data.count))
      .catch((err) => {
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("Something went wrong!", { theme: "colored" });
      });
  };

  const getWishlistCounterData = () => {
    const anonUserId = getAnonUserId();
    axios
      .get(
        `${hostname}/api/wishlist/get-wishlist-count?annoUserDeviceId=${anonUserId}`
      )
      .then((res) => setWishListCounter(res.data.count))
      .catch((err) => {
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("Something went wrong!", { theme: "colored" });
      });
  };

  const addItemtoWishlist = (p_id) => {
    if (!p_id) return;
    const anonUserId = getAnonUserId();
    axios
      .post(`${hostname}/api/wishlist/add-in-wishlist`, {
        product_id: p_id,
        annoUserDeviceId: anonUserId,
      })
      .then(() => {
        setWishListCounter((prev) => prev + 1);
        getAllWishListData();
        try {
          if (ReactPixel && typeof ReactPixel.trackCustom === "function") {
            ReactPixel.trackCustom("AddToWishlist", { product_id: p_id });
          }
        } catch (e) {}
        toast.success("Product added to Wishlist.", { theme: "colored" });
      })
      .catch((err) => {
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("Something went wrong!", { theme: "colored" });
      });
  };

  const removeItemtoWishlist = (p_id) => {
    if (!p_id) return;
    const anonUserId = getAnonUserId();
    axios
      .post(`${hostname}/api/wishlist/remove-from-wishlist`, {
        product_id: p_id,
        annoUserDeviceId: anonUserId,
      })
      .then(() => {
        setWishListCounter((prev) => prev - 1);
        getAllWishListData();
        try {
          if (ReactPixel && typeof ReactPixel.trackCustom === "function") {
            ReactPixel.trackCustom("RemoveFromWishlist", { product_id: p_id });
          }
        } catch (e) {}
        toast.warning("Product removed from Wishlist.", { theme: "colored" });
      })
      .catch((err) => {
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("Something went wrong!", { theme: "colored" });
      });
  };

  const addItemtoCart = (p_id, quantity = 1) => {
    if (!p_id) return;
    const anonUserId = getAnonUserId();
    axios
      .post(`${hostname}/api/cart/add`, {
        product_id: p_id,
        annoUserDeviceId: anonUserId,
        quantity,
      })
      .then(() => {
        setCartListCounter((prev) => prev + 1);
        getAllCartListData();
        try {
          if (ReactPixel && typeof ReactPixel.trackCustom === "function") {
            ReactPixel.trackCustom("AddToCart", { product_id: p_id, quantity });
          }
        } catch (e) {}
        toast.success("Product added to Cart.", { theme: "colored" });
      })
      .catch((err) => {
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("Something went wrong!", { theme: "colored" });
      });
  };

  const removeItemtoCart = (p_id) => {
    if (!p_id) return;
    const anonUserId = getAnonUserId();
    axios
      .post(`${hostname}/api/cart/remove`, {
        product_id: p_id,
        annoUserDeviceId: anonUserId,
      })
      .then(() => {
        setCartListCounter((prev) => prev - 1);
        getAllCartListData();
        try {
          if (ReactPixel && typeof ReactPixel.trackCustom === "function") {
            ReactPixel.trackCustom("RemoveFromCart", { product_id: p_id });
          }
        } catch (e) {}
        toast.warning("Product removed from Cart.", { theme: "colored" });
      })
      .catch((err) => {
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("Something went wrong!", { theme: "colored" });
      });
  };

  const updateQuantity = (productId, newQty) => {
    if (newQty < 1) return;

    const updatedCart = cartList.map((item) => {
      if (item.product._id === productId) {
        return { ...item, quantity: newQty };
      }
      return item;
    });

    setCartList(updatedCart);
  };

  // ✅ helpers
  // const isInCart = (id) => {
  //   console.log(id);

  //   return cartList.some((item) => item._id === id);
  // };
  // const isInWishlist = (id) => {
  //   console.log(id);

  //   return wishList.some((item) => item._id === id);
  // };

  const isInCart = (id) =>
    cartList.some((item) => {
      return item.product?._id === id || item._id === id;
    });

  const isInWishlist = (id) =>
    wishList.some((item) => {
      console.log(id);
      console.log(wishList);

      return item.product?._id === id;
    });

  // load initial data
  useEffect(() => {
    getAllCartListData();
    getAllWishListData();
    getCartCounterData();
    getWishlistCounterData();
  }, []);

  return (
    <CartWishlistContext.Provider
      value={{
        cartList,
        wishList,
        cartListCounter,
        wishListCounter,
        getAllCartListData,
        getAllWishListData,
        getCartCounterData,
        getWishlistCounterData,
        addItemtoCart,
        removeItemtoCart,
        addItemtoWishlist,
        removeItemtoWishlist,
        isInCart,
        isInWishlist,
        updateQuantity,
      }}
    >
      {children}
    </CartWishlistContext.Provider>
  );
};

export const useCartWishlist = () => useContext(CartWishlistContext);
